import { Link } from "@inertiajs/react"
import type { Video } from "@/types"
import { Badge } from "@/components/ui/badge"
import { OptimizedImage } from "@/components/ui/optimized-image"
import VideoActionsMenu from "@/components/dashboard/videos/video-actions-menu"

const FORMAT_LABELS: Record<Video["aspect_ratio"], string> = {
  portrait: "9:16",
  landscape: "16:9",
  square: "1:1",
}

const ASPECT_CLASSES: Record<Video["aspect_ratio"], string> = {
  portrait: "aspect-[9/16]",
  landscape: "aspect-video",
  square: "aspect-square",
}

const STATUS_LABELS: Record<Video["status"], string> = {
  draft: "In progress",
  scripted: "Scripted",
  rendered: "Rendered",
  published: "Published",
  archived: "Archived",
}

const STATUS_STYLES: Record<Video["status"], string> = {
  draft: "bg-stone/60 text-slate",
  scripted: "bg-gold/20 text-gold",
  rendered: "bg-green-100 text-green-700",
  published: "bg-green-100 text-green-700",
  archived: "bg-warm-grey/20 text-warm-grey",
}

const WIZARD_STEPS: Record<string, { label: string; index: number }> = {
  photos: { label: "Photos", index: 0 },
  style: { label: "Style", index: 1 },
  script: { label: "Script", index: 2 },
  audio: { label: "Audio", index: 3 },
  branding: { label: "Branding", index: 4 },
  review: { label: "Review", index: 5 },
}

export default function VideoCard({
  video,
  showAddress = false,
  currentRole,
}: {
  video: Video
  showAddress?: boolean
  currentRole: string
}) {
  const step = WIZARD_STEPS[video.wizard_step] || { label: video.wizard_step, index: 0 }
  const progress = ((step.index + 1) / 6) * 100
  const isDraft = video.status === "draft"

  return (
    <div className="relative">
    <Link
      href={`/dashboard/videos/${video.id}`}
      className="group block overflow-hidden rounded-xl border border-stone/40 bg-white transition-all hover:border-stone hover:shadow-sm"
    >
      {/* Preview area */}
      <div className="flex h-52 items-center justify-center bg-charcoal/[0.03] px-6 py-5">
        <div
          className={`relative h-full max-w-full overflow-hidden rounded-lg bg-charcoal shadow-md shadow-charcoal/15 ring-1 ring-charcoal/10 ${ASPECT_CLASSES[video.aspect_ratio]}`}
        >
          {video.hero_image_url ? (
            <OptimizedImage
              src={video.hero_image_url}
              alt=""
              className="size-full object-cover transition-opacity group-hover:opacity-90"
            />
          ) : (
            <div className="flex size-full items-center justify-center bg-gradient-to-br from-charcoal to-slate">
              <svg className="size-6 text-white/30" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
              </svg>
            </div>
          )}

          {/* Play icon overlay */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
            <div className="flex size-9 items-center justify-center rounded-full bg-white/90 shadow-lg">
              <svg className="ml-0.5 size-4 text-charcoal" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5.14v14l11-7-11-7z" />
              </svg>
            </div>
          </div>

          {/* Format label */}
          <div className="absolute bottom-1.5 right-1.5">
            <span className="rounded bg-black/50 px-1.5 py-0.5 text-[9px] font-semibold tabular-nums text-white/80 backdrop-blur-sm">
              {FORMAT_LABELS[video.aspect_ratio]}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="border-t border-stone/20 px-4 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-charcoal group-hover:text-red">
              {video.name}
            </p>
            {showAddress && video.listing_address && (
              <p className="mt-0.5 truncate text-xs text-warm-grey">
                {video.listing_address}
              </p>
            )}
          </div>
          <Badge className={`shrink-0 text-[10px] ${STATUS_STYLES[video.status]}`}>
            {STATUS_LABELS[video.status]}
          </Badge>
        </div>

        {/* Wizard progress (draft videos) */}
        {isDraft && (
          <div className="mt-2.5">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-[11px] text-warm-grey">
                {step.label}
              </span>
              <span className="text-[11px] font-medium text-slate">
                {step.index + 1}/6
              </span>
            </div>
            <div className="h-1 overflow-hidden rounded-full bg-stone/40">
              <div
                className="h-full rounded-full bg-red/70 transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Meta row */}
        <div className="mt-2.5 flex items-center gap-3 text-[11px] text-warm-grey">
          <span>{video.target_length}s</span>
          {video.script_count > 0 && (
            <>
              <span className="text-stone">·</span>
              <span>{video.script_count} {video.script_count === 1 ? "script" : "scripts"}</span>
            </>
          )}
          {video.selected_images.length > 0 && (
            <>
              <span className="text-stone">·</span>
              <span>{video.selected_images.length} photos</span>
            </>
          )}
          <span className="ml-auto">{formatRelativeTime(video.updated_at)}</span>
        </div>
      </div>
    </Link>
    <div className="absolute right-2 top-2">
      <VideoActionsMenu video={video} currentRole={currentRole} />
    </div>
    </div>
  )
}

function formatRelativeTime(dateStr: string | Date): string {
  const date = typeof dateStr === "string" ? new Date(dateStr) : dateStr
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return "just now"
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" })
}
